import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  locationSalesGetSalesByCategory,
  type SaleByCategoryEntry,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { toConvexTableId } from "~/lib/convex-table-id";

type ExtendedCategory = SaleByCategoryEntry;

interface CategoryViewProps {
  locationId: string;
  dateRange: {
    from: Date;
    to: Date;
  };
  tableId: string;
}

export const CategoryView = ({
  locationId,
  dateRange,
  tableId,
}: CategoryViewProps) => {
  const { data = [], isPending } = useQuery(
    convexQuery(locationSalesGetSalesByCategory, {
      dateRange: {
        from: dateRange.from.getTime(),
        to: dateRange.to.getTime(),
      },
      locationId: toConvexTableId("locations", locationId),
    })
  );
  const isLoading = isPending;

  const columnHelper = createColumnHelper<ExtendedCategory>();

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("categoryName", {
          header: "Category Name",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.some((filter) =>
              original.categoryName.toLowerCase().includes(filter.toLowerCase())
            ),
        }),
        columnHelper.accessor("orderType", {
          header: "Order Type",
          cell: ({ getValue }) => getValue(),
          filterFn: ({ original }, _, filterValue: string[]) =>
            filterValue.includes(original.orderType),
          meta: {
            faceting: {
              title: "Order Type",
              getOption: (orderType: ExtendedCategory["orderType"]) => ({
                value: orderType,
                label: orderType,
              }),
            },
          },
        }),
        columnHelper.accessor("itemsSold", {
          header: "Items Sold",
          cell: ({ getValue }) => getValue().toLocaleString(),
          meta: {
            getCellForExport: (value: ExtendedCategory["itemsSold"]) => value,
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue().toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (value: ExtendedCategory["amountSold"]) => value,
          },
        }),
        columnHelper.accessor("shareOfTotalSales", {
          header: "% of Total Sales",
          cell: ({ getValue }) => `${(getValue() * 100).toFixed(1)}%`,
          meta: {
            getCellForExport: (value: ExtendedCategory["shareOfTotalSales"]) =>
              value,
          },
        }),
      ] as ColumnDef<ExtendedCategory>[],
    [columnHelper]
  );

  if (isLoading) {
    return <LoadingPanel message="Loading category performance data..." />;
  }

  return <SalesDataTable columns={columns} data={data} id={tableId} />;
};
